import {
	SetUserLicenseAudience,
	SetUserLicenseForUsersModel,
	UserLicense,
	setUserLicenseForUsers
} from "client/Api/FeaturePackages/featurePackageManagement";
import {
	Dialog,
	Form,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	Toolbar,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import { isNotNullNorUndefined } from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useMemo,
	useState
} from "react";
import { getUserLicenseName } from "client/Features/FeaturePackages/misc";
import { useChangeUserLicenseConfirmationDialog } from "../UserLicenseManagement/ChangeUserLicenseConfirmationDialog";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";

export type ManageUserLicensesDialogProps = {
	audience: SetUserLicenseAudience;
	notAvailableLicense: UserLicense | undefined;
	onClose(): void;
	onSave(): void;
};

export const ManageUserLicensesDialog = (props: ManageUserLicensesDialogProps): ReactElement => {
	const {
		audience,
		notAvailableLicense,
		onClose,
		onSave
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<FormData>(
		(): FormData => {
			const availableOptions: RadioButtonGroupItem<UserLicense>[] = licenseOptions.filter(
				o => isNil(notAvailableLicense)
					|| o.value !== notAvailableLicense
			);

			if (availableOptions.length === 1) {
				return {
					selectedLicense: availableOptions[0].value
				};
			}

			return DEFAULT_FORM_DATA;
		}
	);

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const [confirmationDialog, openConfirmationDialog] = useChangeUserLicenseConfirmationDialog({
		title: localize("_AreYouSureYouWantToChangeWhichPackageIsAssignedToTheUsers"),
		trackingId: "change-package-confirmation-dialog"
	});

	const computedLicenseOptions = useMemo(
		(): readonly RadioButtonGroupItem<UserLicense>[] => {
			if (isNil(notAvailableLicense)) {
				return licenseOptions;
			}

			return licenseOptions.map((option) => ({
				...option,
				isDisabled: option.value === notAvailableLicense
			}));

		},
		[notAvailableLicense]
	);

	const submitHandler = async (): Promise<void> => {
		if (isBusy) {
			return;
		}

		setIsBusy(true);

		try {
			const validationResult: InputValidationResult = await formHandler.validation
				.validate(globalCancellation);

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				setIsBusy(false);
				return;
			}

			if (!await openConfirmationDialog()) {
				setIsBusy(false);
				return;
			}

			const model: SetUserLicenseForUsersModel = {
				userLicense: ensureNotNil(formData.selectedLicense),
				audience: audience
			};

			await setUserLicenseForUsers(
				model,
				globalCancellation
			);

			onSave();
		} catch (e) {
			if (isCancelledError(e)) {
				return;
			}

			if (!formHandler.validation.tryProcessServerError(e)) {
				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			}

			setIsBusy(false);
		}
	};

	if (isNotNil(confirmationDialog)) {
		return confirmationDialog;
	}

	return (
		<Dialog
			trackingId="change-package-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_Package")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem
						label={localize("_ApplyAPackageToAllSelectedUsers")}
						isForGroup
					>
						<RadioButtonGroup
							{...formHandler.for("selectedLicense").attachToRadioButtonGroup()}
							options={computedLicenseOptions}
						/>
						<ValidationError
							{...formHandler.for("selectedLicense").attachToValidationError()}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Apply")}
						onClick={submitHandler}
						trackingId="apply"
					/>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ManageUserLicensesDialog.displayName = "ManageUserLicensesDialog";

type FormData = {
	selectedLicense: UserLicense | null;
};

const DEFAULT_FORM_DATA: FormData = {
	selectedLicense: null
};

const licenseOptions: readonly RadioButtonGroupItem<UserLicense>[] = [
	{
		value: UserLicense.Simplify,
		text: getUserLicenseName(UserLicense.Simplify)
	},
	{
		value: UserLicense.Connect,
		text: getUserLicenseName(UserLicense.Connect)
	}
];

const validationConfiguration = createValidationConfiguration<FormData>();

validationConfiguration
	.forScalar("selectedLicense")
	.add(isNotNullNorUndefined());

export type ManageUserLicensesDialogInitialData = Omit<ManageUserLicensesDialogProps, "onClose" | "onSave">;

export function useManageUserLicensesDialog(): [
	element: ReactElement | null,
	open: (props: ManageUserLicensesDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onSave) => (
			<ManageUserLicensesDialog
				{...props}
				onClose={onClose}
				onSave={onSave}
			/>
		)
	);
}

